import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import config from '../config';
import { getOrganizationContext, getOrganizationHeaders } from '../orgContext';

interface TeachingAssignmentRow {
  id: number;
  teacherDisplayName?: string | null;
  studentDisplayName?: string | null;
  courseName?: string | null;
  courseId?: number | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
}

interface ScheduleSessionRow {
  id: number;
  title?: string | null;
  teacherDisplayName?: string | null;
  studentDisplayName?: string | null;
  classroomName?: string | null;
  status?: string | null;
  startDateTime?: string | null;
  endDateTime?: string | null;
  meetingUrl?: string | null;
  joinUrl?: string | null;
  location?: string | null;
}

const normalizeName = (value?: string | null): string =>
  (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const statusTone = (status?: string | null): { background: string; color: string } => {
  const normalized = (status ?? '').toLowerCase();

  if (normalized.includes('complete')) {
    return { background: '#dcfce7', color: '#166534' };
  }

  if (normalized.includes('cancel') || normalized.includes('miss')) {
    return { background: '#fee2e2', color: '#991b1b' };
  }

  if (normalized.includes('progress') || normalized.includes('in')) {
    return { background: '#dbeafe', color: '#1d4ed8' };
  }

  return { background: '#fef3c7', color: '#92400e' };
};

const TeacherDashboard: React.FC = () => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const organizationContext = getOrganizationContext();
  const organizationName = organizationContext?.organizationName || 'Current organization';

  const [teacherAssignments, setTeacherAssignments] = useState<TeachingAssignmentRow[]>([]);
  const [sessions, setSessions] = useState<ScheduleSessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentUserName = useMemo(
    () => [user?.name, user?.nickname, user?.email].find((value) => Boolean(value)) ?? '',
    [user]
  );
  const normalizedUserName = normalizeName(currentUserName);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...getOrganizationHeaders(),
        };

        const [assignmentsResponse, scheduleResponse] = await Promise.all([
          fetch(`${config.apiBaseUrl}/api/teachingassignments`, { method: 'GET', headers }),
          fetch(`${config.apiBaseUrl}/api/schedules/my?upcomingOnly=false`, { method: 'GET', headers }),
        ]);

        const rawAssignments = assignmentsResponse.ok ? await assignmentsResponse.json() : [];
        const rawSessions = scheduleResponse.ok ? await scheduleResponse.json() : [];

        const assignments = Array.isArray(rawAssignments)
          ? rawAssignments.filter((row: TeachingAssignmentRow) => {
              if (!normalizedUserName) {
                return true;
              }

              const teacherName = normalizeName(row.teacherDisplayName);
              const studentName = normalizeName(row.studentDisplayName);
              return teacherName === normalizedUserName || teacherName.includes(normalizedUserName) || normalizedUserName.includes(teacherName) || studentName.includes(normalizedUserName) || normalizedUserName.includes(studentName);
            })
          : [];

        const teacherSessions = Array.isArray(rawSessions)
          ? rawSessions.filter((row: ScheduleSessionRow) => {
              if (!normalizedUserName) {
                return true;
              }

              const teacherName = normalizeName(row.teacherDisplayName);
              return teacherName === normalizedUserName || teacherName.includes(normalizedUserName) || normalizedUserName.includes(teacherName);
            })
          : [];

        setTeacherAssignments(assignments);
        setSessions(teacherSessions);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load teacher dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    void loadDashboardData();
  }, [getAccessTokenSilently, isAuthenticated, normalizedUserName]);

  const upcomingSessions = useMemo(
    () =>
      sessions
        .filter((session) => {
          const startDate = session.startDateTime ? new Date(session.startDateTime).getTime() : Number.NEGATIVE_INFINITY;
          const endDate = session.endDateTime ? new Date(session.endDateTime).getTime() : Number.POSITIVE_INFINITY;
          return Number.isFinite(startDate) && endDate >= Date.now();
        })
        .sort((first, second) => new Date(first.startDateTime ?? 0).getTime() - new Date(second.startDateTime ?? 0).getTime())
        .slice(0, 5),
    [sessions]
  );

  const completedSessions = useMemo(
    () => sessions.filter((session) => (session.status ?? '').toLowerCase().includes('complete')).length,
    [sessions]
  );

  const studentsAssigned = useMemo(
    () => new Set(teacherAssignments.map((assignment) => assignment.studentDisplayName).filter(Boolean)).size,
    [teacherAssignments]
  );

  const assignmentCards = [
    { label: 'Sessions completed', value: completedSessions, hint: 'Completed this term' },
    { label: 'Students assigned', value: studentsAssigned, hint: 'Linked to you' },
    { label: 'Assignments', value: teacherAssignments.length, hint: 'Currently in scope' },
    { label: 'Upcoming sessions', value: upcomingSessions.length, hint: 'Next few meetings' },
  ];

  const assignmentTableRows = useMemo(
    () =>
      teacherAssignments
        .slice()
        .sort((first, second) => {
          const left = new Date(first.startDate ?? 0).getTime();
          const right = new Date(second.startDate ?? 0).getTime();
          return right - left;
        })
        .slice(0, 8),
    [teacherAssignments]
  );

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 24, width: '100%', color: '#475569' }}>
        Please sign in to view your teacher dashboard.
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: '100%', padding: 0, background: '#f8fafc' }}>
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>
          Teacher workspace
        </div>
        <h1 style={{ margin: '8px 0 0', color: '#173159', fontSize: 34, fontWeight: 800 }}>Teacher dashboard</h1>
        <div style={{ marginTop: 6, color: '#475569', fontSize: 16 }}>{organizationName}</div>
      </div>

      {error ? (
        <div style={{ marginBottom: 20, background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 12, padding: 14, color: '#9f1239' }}>
          {error}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 18, marginBottom: 28 }}>
        {assignmentCards.map((card) => (
          <div
            key={card.label}
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 16,
              boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
              padding: 20,
            }}
          >
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>{card.label}</div>
            <div style={{ fontSize: 30, fontWeight: 800, color: '#173159', marginBottom: 6 }}>{card.value}</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>{card.hint}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(260px, 1fr)', gap: 20, marginBottom: 24, width: '100%' }}>
        <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 20, boxShadow: '0 8px 22px rgba(15, 23, 42, 0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, color: '#173159', fontSize: 22 }}>Upcoming Sessions</h2>
            <span style={{ color: '#64748b', fontSize: 12 }}>{upcomingSessions.length} scheduled</span>
          </div>

          {loading ? (
            <div style={{ color: '#64748b', padding: '16px 0' }}>Loading teacher schedule…</div>
          ) : upcomingSessions.length === 0 ? (
            <div style={{ color: '#64748b', padding: '16px 0' }}>No upcoming sessions scheduled.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 8px' }}>Date</th>
                    <th style={{ padding: '10px 8px' }}>Time</th>
                    <th style={{ padding: '10px 8px' }}>Topic</th>
                    <th style={{ padding: '10px 8px' }}>Student</th>
                    <th style={{ padding: '10px 8px' }}>Join</th>
                    <th style={{ padding: '10px 8px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingSessions.map((session) => {
                    const joinLink = session.meetingUrl || session.joinUrl || '';
                    const isLinkAvailable = Boolean(joinLink);
                    const tone = statusTone(session.status);

                    return (
                      <tr key={session.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 8px', color: '#1e293b' }}>{formatDate(session.startDateTime)}</td>
                        <td style={{ padding: '10px 8px', color: '#1e293b' }}>{formatTime(session.startDateTime)}</td>
                        <td style={{ padding: '10px 8px', color: '#1e293b' }}>{session.title || session.classroomName || 'Session'}</td>
                        <td style={{ padding: '10px 8px', color: '#1e293b' }}>{session.studentDisplayName || 'Classroom'}</td>
                        <td style={{ padding: '10px 8px' }}>
                          {isLinkAvailable ? (
                            <a
                              href={joinLink}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}
                            >
                              Join
                            </a>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span
                            style={{
                              display: 'inline-block',
                              background: tone.background,
                              color: tone.color,
                              borderRadius: 999,
                              padding: '4px 8px',
                              fontWeight: 700,
                              fontSize: 11,
                              textTransform: 'capitalize',
                            }}
                          >
                            {session.status || 'Scheduled'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 20, boxShadow: '0 8px 22px rgba(15, 23, 42, 0.04)' }}>
          <h2 style={{ margin: '0 0 16px', color: '#173159', fontSize: 22 }}>Quick actions</h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {[
              { label: 'Manage assignments', to: '/manage-assignments' },
              { label: 'Manage exams', to: '/manage-exams' },
              { label: 'Manage schedule', to: '/manage-schedule' },
            ].map((link) => (
              <Link
                key={link.label}
                to={link.to}
                style={{
                  display: 'block',
                  padding: '12px 14px',
                  borderRadius: 12,
                  color: '#173159',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  textDecoration: 'none',
                  fontWeight: 600,
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </aside>
      </div>

      <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 20, boxShadow: '0 8px 22px rgba(15, 23, 42, 0.04)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, color: '#173159', fontSize: 22 }}>Assignments</h2>
          <span style={{ color: '#64748b', fontSize: 12 }}>{assignmentTableRows.length} showing</span>
        </div>

        {loading ? (
          <div style={{ color: '#64748b', padding: '12px 0' }}>Loading assignments…</div>
        ) : assignmentTableRows.length === 0 ? (
          <div style={{ color: '#64748b', padding: '12px 0' }}>No assignments linked to your teaching scope yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '10px 8px' }}>Date</th>
                  <th style={{ padding: '10px 8px' }}>Student</th>
                  <th style={{ padding: '10px 8px' }}>Topic</th>
                  <th style={{ padding: '10px 8px' }}>Due</th>
                  <th style={{ padding: '10px 8px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {assignmentTableRows.map((assignment) => {
                  const tone = statusTone(assignment.status);
                  return (
                    <tr key={assignment.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '10px 8px', color: '#1e293b' }}>{formatDate(assignment.startDate)}</td>
                      <td style={{ padding: '10px 8px', color: '#1e293b' }}>{assignment.studentDisplayName || 'Unassigned'}</td>
                      <td style={{ padding: '10px 8px', color: '#1e293b' }}>{assignment.courseName || 'Course'}</td>
                      <td style={{ padding: '10px 8px', color: '#1e293b' }}>{formatDate(assignment.endDate)}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            background: tone.background,
                            color: tone.color,
                            borderRadius: 999,
                            padding: '4px 8px',
                            fontWeight: 700,
                            fontSize: 11,
                            textTransform: 'capitalize',
                          }}
                        >
                          {assignment.status || 'Active'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default TeacherDashboard;
